import React, { useState, useEffect, useRef } from "react";
import { Clock, X, ChevronUp, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TimeBox = ({
    initialTime,
    onApply,
    onClose
}) => {
    // Parse initial time (expecting HH:mm format or Date object)
    const getInitialValues = () => {
        if (!initialTime) return { hr: "09", min: "00", ampm: "AM" };

        const date = new Date(initialTime);
        if (isNaN(date.getTime())) {
            // Try parsing HH:mm string
            const parts = initialTime.split(":");
            if (parts.length >= 2) {
                let hr = parseInt(parts[0]);
                const min = parts[1].padStart(2, '0');
                const ampm = hr >= 12 ? "PM" : "AM";
                hr = hr % 12 || 12;
                return { hr: hr.toString().padStart(2, '0'), min, ampm };
            }
            return { hr: "09", min: "00", ampm: "AM" };
        }

        let hr = date.getHours();
        const min = date.getMinutes().toString().padStart(2, '0');
        const ampm = hr >= 12 ? "PM" : "AM";
        hr = hr % 12 || 12;
        return { hr: hr.toString().padStart(2, '0'), min, ampm };
    };

    const initialValues = getInitialValues();
    const [hour, setHour] = useState(initialValues.hr);
    const [minute, setMinute] = useState(initialValues.min);
    const [ampm, setAmpm] = useState(initialValues.ampm);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const isFirstMount = useRef(true);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        // Auto-apply whenever values change
        let hr = parseInt(hour);
        if (ampm === "PM" && hr !== 12) hr += 12;
        if (ampm === "AM" && hr === 12) hr = 0;

        const date = new Date();
        date.setHours(hr, parseInt(minute), 0, 0);

        // Use a small timeout to avoid synchronous state updates in parent during render
        const timer = setTimeout(() => {
            onApply(date, false);
        }, 0);
        return () => clearTimeout(timer);
    }, [hour, minute, ampm]);

    const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

    const Column = ({ title, items, value, onChange, className = "" }) => (
        <div className={`flex flex-col items-center gap-1 ${className}`}>
            <span className="text-[10px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest mb-4">{title}</span>
            <div className="h-48 overflow-y-auto px-2 py-2 space-y-1 scrollbar-hide
                [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-transparent">
                {items.map((item) => (
                    <button
                        key={item}
                        onClick={() => onChange(item)}
                        className={`w-12 h-10 flex items-center justify-center rounded-xl text-sm font-bold transition-all duration-300
                            ${value === item
                                ? "bg-teal-600 text-white shadow-[0_0_20px_rgba(20,184,166,0.3)] scale-105 z-10"
                                : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"}`}
                    >
                        {item}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <motion.div
            ref={modalRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 lg:left-auto lg:right-0 mt-2 bg-white dark:bg-[#111111] rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden min-w-[320px] border border-gray-100 dark:border-white/5 z-[100]"
        >
            <div className="p-10 grid grid-cols-3 gap-6">
                <Column title="Hours" items={hours} value={hour} onChange={setHour} />
                <Column title="Minutes" items={minutes} value={minute} onChange={setMinute} />
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-6">Period</span>
                    <div className="flex flex-col gap-3 py-2">
                        {["AM", "PM"].map((item) => (
                            <button
                                key={item}
                                onClick={() => setAmpm(item)}
                                className={`w-16 h-14 flex items-center justify-center rounded-2xl text-[15px] font-black transition-all duration-300
                                    ${ampm === item
                                        ? "bg-teal-600 text-white shadow-[0_0_25px_rgba(20,184,166,0.4)] scale-110 z-10"
                                        : "text-gray-400 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-white/5"}`}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default TimeBox;
